import * as React from 'react';
import { NegotiationOption, NegotiationOptionAlternative, NegotiationOptionExtraConfirmationInfoType, OutDebt } from '../../generated/graphql';
import NonPaymentReasonForm from './DebtDetailsNegotiationRequestsContent/NonPaymentReasonForm';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { hasNoPaymentReasonOptions } from '../../utils/getNoPaymentReasonOptions';
import { useLocalAdminInfo } from '../../utils/useLocalAdminInfo';

interface DebtDtlsNegSummaryFormProps {
  onConfirm: (values: any) => void;
  negOption: NegotiationOption;
  alternative?: NegotiationOptionAlternative;
  outDebt: OutDebt;
  outDebts?: OutDebt[];
}

const DebtDetailsNegotiationSummaryForm: React.FC<DebtDtlsNegSummaryFormProps> = (props) => {
  const { children, onConfirm, negOption, alternative, outDebt, outDebts } = props;
  const { extraConfirmationInfo } = negOption;
  const [ adminInfo ] = useLocalAdminInfo();

  const {
    isBbva, isInstallmentsPlan, isBeta,
    isTranqui, isDav,
    isBogota, isScotiabank, isRefinance,
    isBogotaTermExtension,
  } = getNegBooleans(outDebt, negOption);

  const noForm = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType.NoExtraInfo;
  const isNPR = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .NonPaymentReason; // refinance
  const isNPRCNPD = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .NonPaymentReasonCancellationNewPaymentDay; // normalization
  const isNPRPADWA = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .NonPaymentReasonPaymentAndDateWithAgreement; // acpk / penaly portfolio
  const isNPRPD = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .NonPaymentReasonPaymentDate; // total payment
  const isUserConfirmation = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .UserConfirmation;
  const isEconomyActivity = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .EconomicActivity;
  const isNewPaymentDate = extraConfirmationInfo === NegotiationOptionExtraConfirmationInfoType
    .NewPaymentDate;
  const isPrePaymentDateAndInstallmentDay = extraConfirmationInfo
  === NegotiationOptionExtraConfirmationInfoType.PrePaymentDateAndInstallmentDay;

  const isScotiabankRefinance = isScotiabank && isRefinance;
  const isBbvaInstallmentsPlan = isBbva && isInstallmentsPlan;
  const hasNoPaymentReason = isNPR
    && hasNoPaymentReasonOptions(outDebt.bank, outDebt.brand);

  const showNonPaymentReasonForm = !noForm
    && (
      isNPR || isNPRCNPD || isNPRPD || isNPRPADWA
      || isUserConfirmation || isEconomyActivity
      || isNewPaymentDate || isPrePaymentDateAndInstallmentDay
    );

  if (showNonPaymentReasonForm) {
    return (
      <NonPaymentReasonForm
        withUserConfirmation={isUserConfirmation}
        withCancel={(isNPRCNPD || isNPRPADWA) && (isDav || isBeta || isBogota)}
        withConditions={isBbvaInstallmentsPlan}
        withNewPaymentDay={(
            ((isNPRCNPD || isNPRPADWA) && (isDav || isBeta))
            || isPrePaymentDateAndInstallmentDay
        )}
        withAutoDebit={isBbva && isTranqui}
        withPaymentCapacity={isScotiabankRefinance}
        withPaymentDate={isNewPaymentDate || isPrePaymentDateAndInstallmentDay}
        withTermExtensionSox={isBogotaTermExtension && !!adminInfo.token}
        withNoPaymentReason={hasNoPaymentReason}
        onConfirm={onConfirm}
        negotiation={negOption}
        outDebt={outDebt}
        alternative={alternative}
        outDebts={outDebts}
      >
        {children}
      </NonPaymentReasonForm>
    );
  }

  const handleConfirm: React.DOMAttributes<HTMLFormElement>['onSubmit'] = (event) => {
    event.preventDefault();
    onConfirm({});
  };

  return (
    <form onSubmit={handleConfirm}>
      {children}
    </form>
  );
};

export default DebtDetailsNegotiationSummaryForm;
